import { useEffect } from 'react'

import { useRequest } from 'ahooks'
import { pick } from 'lodash'

import { queryDeptUserTreeApi } from '@eastcoal/api/auth/dept/queryDeptUserTree'
import { DeptUser } from '@eastcoal/api/auth/interface'

import { KeySet } from '@eastcoal/common/types/utilities'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'

import { useDeptUserBaseStoreApi } from '../hooks/useStore'
import { DeptUserBaseAction, DeptUserBaseState } from '../store/interface'

import { DeptUserBaseStoreUpdaterProps } from './interface'

const useStoreUpdater = <M, K extends KeySet<DeptUserBaseState<M>>>(
  key: K,
  value?: DeptUserBaseState<M>[K]
) => {
  const { setState } = useDeptUserBaseStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const useStoreSetter = <
  LabelInValue extends boolean,
  M extends boolean,
  K extends KeySet<DeptUserBaseAction<LabelInValue, M>>
>(
  setter: K,
  value: unknown
) => {
  const { getState } = useDeptUserBaseStoreApi()
  useEffect(() => {
    getState()[setter](value as any)
  }, [setter, value, getState])
}

const EMPTY_ARRAY: DeptUser[] = []

export const DeptUserBaseStoreUpdater = <M>({
  /* ---------------------------------------------------------------------------- */
  /*                                DeptUserBaseStore                             */
  /* ---------------------------------------------------------------------------- */
  multiple,
  triggerChangeWhenSelect,
  value,
  onChange,

  /* -------------------------------------------------------------------------- */
  /*                               Api Parameters                               */
  /* -------------------------------------------------------------------------- */
  strictMode = true
}: DeptUserBaseStoreUpdaterProps<M>) => {
  const {
    member: { id: memberId },
    dept: { id: deptId }
  } = useAuthStore(state => pick(state, ['member', 'dept']))

  const {
    data = EMPTY_ARRAY,
    error,
    loading,
    refreshAsync
  } = useRequest(
    () =>
      queryDeptUserTreeApi({
        memberId,
        deptId: strictMode ? deptId : undefined
      }),
    {
      manual: false,
      ready: !!memberId,
      refreshDeps: [strictMode, memberId]
    }
  )

  useStoreUpdater('data', data)
  useStoreUpdater('error', error)
  useStoreUpdater('loading', loading)
  useStoreUpdater('reload', refreshAsync)

  useStoreUpdater('multiple', multiple)
  useStoreUpdater('triggerChangeWhenSelect', triggerChangeWhenSelect)
  useStoreUpdater<M, 'onChange'>('onChange', onChange)

  useStoreSetter('setValue', value)

  return null
}
